import React, { useState, useEffect } from 'react';
import { useRAGStore } from '../store/ragStore';
import { FileText, Trash2, Play, Database, Edit3, RefreshCw, ChevronDown, ChevronRight, Tag } from 'lucide-react';

const FileListTab: React.FC = () => {
  const {
    currentContext,
    getChunks,
    uploadChunks,
    buildChunks,
    buildEmbeddings,
    deleteItem,
    refreshCurrentContext,
    isOperationInProgress,
    queueStatus,
    getQueueStatus,
    getAllTasks 
  } = useRAGStore();

  const [processingItems, setProcessingItems] = useState<Set<string>>(new Set());
  const [chunkResults, setChunkResults] = useState<Record<string, any>>({});
  const [editingChunks, setEditingChunks] = useState<Record<string, string>>({});
  const [showChunkEditor, setShowChunkEditor] = useState<string | null>(null);
  const [expandedLabels, setExpandedLabels] = useState<Set<string>>(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false); // 🔹 Prevent concurrent refresh
  // const [queueStatus, setQueueStatus] = useState<any>(null);
  const [runningTasks, setRunningTasks] = useState<any[]>([]);
  const [getChunkError, setGetChunkError] = useState<Record<string, string>>({});

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const pollTasks = async () => {
      try {
        const tasks = await getAllTasks(50); 
        const active = tasks.filter((t: any) => {
          const s = t.status?.toLowerCase();
          return s === "pending" || s === "processing";
        });
        setRunningTasks(active);
      } catch (err) {
        console.error("Failed to fetch tasks:", err);
      }
    };

    pollTasks(); // Execute first time
    interval = setInterval(pollTasks, 2000);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [getAllTasks]);

  useEffect(() => {
    setProcessingItems(new Set());
  }, [currentContext?.contextId]);

  if (!currentContext) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Context Selected</h3>
          <p className="text-gray-600">Please go to the Context Management tab to select a context before viewing files.</p>
        </div>
      </div>
    );
  }

  /** 🔹 Encapsulate refresh function: prevent concurrency */
  const handleRefresh = async () => {
    if (isRefreshing || isOperationInProgress) return;
    setIsRefreshing(true);
    try {
      await refreshCurrentContext();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOpenChunkEditor = async (itemId: string) => {
    setGetChunkError(prev => ({ ...prev, [itemId]: "" })); // Clear old error

    try {
      const result = await getChunks(itemId);
      if (result) {
        // Assume result is the JSON chunks returned from backend
        setChunkResults(prev => ({ ...prev, [itemId]: result }));
        setEditingChunks(prev => ({
          ...prev,
          [itemId]: JSON.stringify(result, null, 2),
        }));
        setShowChunkEditor(itemId);
      }
    } catch (err: any) {
      // ❌ If getChunks throws an error, catch it and store in local state
      const msg =
        (err && err.message) || "Failed to get chunks. Please try again.";
      setGetChunkError(prev => ({ ...prev, [itemId]: msg }));
    }
  };

  /** 🔹 Build Chunks */
  const handleBuildChunks = async (itemId: string) => {
    if (isOperationInProgress) return;
    if (processingItems.has(`chunk-${itemId}`)) return;

    setProcessingItems(prev => new Set(prev).add(`chunk-${itemId}`));
    try {
      const result = await buildChunks(itemId);
      if (result && result.chunks) {
        setChunkResults(prev => ({ ...prev, [itemId]: result.chunks }));
        setEditingChunks(prev => ({
          ...prev,
          [itemId]: JSON.stringify(result.chunks, null, 2),
        }));
        setShowChunkEditor(itemId);
      }
      await handleRefresh();
    } catch (error) {
      console.error('Failed to build chunks:', error);
      await handleRefresh();
    } finally {
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(`chunk-${itemId}`);
        return newSet;
      });
    }
  };

  /** 🔹 Save and Embed */
  const handleSaveChunks = async (itemId: string) => {
    if (isOperationInProgress) return;
    if (processingItems.has(`save-embed-${itemId}`)) return;

    setProcessingItems(prev => new Set(prev).add(`save-embed-${itemId}`));
    try {
      const editedChunks = JSON.parse(editingChunks[itemId]);
      await uploadChunks(itemId, editedChunks);

      setShowChunkEditor(null);
      setEditingChunks(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      setChunkResults(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });

      await handleRefresh();
    } catch (error) {
      console.error('Failed to save chunks:', error);
      await handleRefresh();
    } finally {
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(`save-embed-${itemId}`);
        return newSet;
      });
    }
  };

  /** 🔹 Build Embeddings */
  const handleBuildEmbeddings = async (itemId: string) => {
    if (isOperationInProgress) return;
    if (processingItems.has(`embed-${itemId}`)) return;

    let editedChunks: any | undefined = undefined;
    if (editingChunks[itemId]) {
      try {
        editedChunks = JSON.parse(editingChunks[itemId]);
      } catch {
        alert('Invalid chunk JSON. Please fix the JSON.');
        return;
      }
    }

    setProcessingItems(prev => new Set(prev).add(`embed-${itemId}`));
    try {
      await buildEmbeddings(itemId, editedChunks);
      if (showChunkEditor === itemId) setShowChunkEditor(null);
      setEditingChunks(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      setChunkResults(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    } catch (error) {
      console.error('Failed to build embeddings:', error);
    } finally {
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(`embed-${itemId}`);
        return newSet;
      });
    }
  };

  /** 🔹 Delete Item */
  const handleDeleteItem = async (itemId: string) => {
    if (!window.confirm('Are you sure to delete this item?')) return;
    if (isOperationInProgress) return;
    if (processingItems.has(`delete-${itemId}`)) return;

    setProcessingItems(prev => new Set(prev).add(`delete-${itemId}`));
    try {
      await deleteItem(itemId);
      setEditingChunks(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      setChunkResults(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      if (showChunkEditor === itemId) setShowChunkEditor(null);
      setTimeout(() => handleRefresh(), 500);
    } catch {
      setTimeout(() => handleRefresh(), 500);
    } finally {
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(`delete-${itemId}`);
        return newSet;
      });
    }
  };

  /** Status color */
  const getStatusColor = (status: string) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    const s = status.toLowerCase();
    if (s === 'valid') return 'bg-green-100 text-green-800';
    if (s === 'queued') return 'bg-blue-100 text-blue-800';
    if (s.includes('process') || s.includes('pending') || ['uploading', 'chunking', 'embedding'].includes(s))
      return 'bg-yellow-100 text-yellow-800';
    if (s.includes('error') || s.includes('fail') || s === 'invalid')
      return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  /** 🔹 Prioritize local state when rendering file cards */
  const renderStatus = (item: any) => {
    const localProcessing =
      processingItems.has(`chunk-${item.ragId}`) ||
      processingItems.has(`embed-${item.ragId}`) ||
      processingItems.has(`save-embed-${item.ragId}`) ||
      processingItems.has(`delete-${item.ragId}`);
    const displayStatus = localProcessing ? 'processing' : (item.status || 'unknown');
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(displayStatus)}`}>
        {displayStatus}
      </span>
    );
  };

  /** Label expansion */
  const toggleLabelsExpansion = (itemId: string) => {
    setExpandedLabels(prev => {
      const newSet = new Set(prev);
      newSet.has(itemId) ? newSet.delete(itemId) : newSet.add(itemId);
      return newSet;
    });
  };

  const renderLabels = (item: any) => {
    if (!item.contentLabels || item.contentLabels.length === 0) {
      return <div className="text-sm text-gray-400 italic py-2">No labels available</div>;
    }

    const isExpanded = expandedLabels.has(item.ragId);
    const displayLabels = isExpanded ? item.contentLabels : item.contentLabels.slice(0, 6);
    const hasMore = item.contentLabels.length > 6;

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {displayLabels.map((label: string, index: number) => (
            <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <Tag className="h-3 w-3 mr-1.5" />
              <span className="truncate max-w-[200px]">{label}</span>
            </span>
          ))}
        </div>
        {hasMore && (
          <button
            onClick={() => toggleLabelsExpansion(item.ragId)}
            className="flex items-center text-xs text-blue-600 hover:text-blue-800 transition-colors py-1 px-2 rounded hover:bg-blue-50"
          >
            {isExpanded ? <><ChevronDown className="h-3 w-3 mr-1" /> Show less</> : <><ChevronRight className="h-3 w-3 mr-1" /> +{item.contentLabels.length - 6} more</>}
          </button>
        )}
      </div>
    );
  };

  if (!currentContext.ragItems || currentContext.ragItems.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
          <FileText className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No files uploaded yet</h3>
        <p className="text-gray-600">Go to the File Upload tab to upload documents</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ✅ Tasks */}
      {runningTasks.map((task) => (
        <div
          key={task.task_id}
          className="flex justify-between items-center border rounded px-3 py-2 text-sm"
        >
          <div>
            <div className="font-medium text-gray-800 truncate">
              {task.file_name || task.fileName || "Unknown File"}
            </div>
            <div className="text-xs text-gray-500">
              Context: {task.context_id}
            </div>
          </div>
          <span
            className={`px-2 py-1 text-xs rounded-full ${
              task.status?.toLowerCase() === "processing"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-blue-100 text-blue-800"
            }`}
          >
            {task.status}
          </span>
        </div>
      ))}

      {/* ✅ File Summary */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">File Summary</h3>
          <button
            onClick={handleRefresh}
            disabled={isOperationInProgress || isRefreshing}
            className={`btn-secondary flex items-center space-x-2 ${
              (isOperationInProgress || isRefreshing) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isRefreshing ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span>Refresh</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{currentContext.ragItems?.length || 0}</div>
            <div className="text-sm text-gray-600">Total Files</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {currentContext.ragItems?.filter(item => item.status && item.status.toLowerCase() === 'valid').length || 0}
            </div>
            <div className="text-sm text-gray-600">Valid/Ready</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {currentContext.ragItems?.filter(item =>
                item.status && (
                  item.status.toLowerCase() === 'processing' ||
                  item.status.toLowerCase().includes('pending') ||
                  ['uploading','chunking','embedding'].includes(item.status.toLowerCase())
                )
              ).length || 0}
            </div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {currentContext.ragItems?.filter(item =>
                item.status && (
                  item.status.toLowerCase() === 'failed' ||
                  item.status.toLowerCase() === 'error' ||
                  item.status.toLowerCase() === 'invalid'
                )
              ).length || 0}
            </div>
            <div className="text-sm text-gray-600">Failed</div>
          </div>
        </div>
      </div>

      {/* ✅ File List (maintain original functionality + buttons available) */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Uploaded Files</h3>
        <div className="space-y-4">
          {currentContext.ragItems.map((item: any) => (
            <div
              key={item.ragId}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => handleOpenChunkEditor(item.ragId)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3 flex-1">
                  <FileText className="h-6 w-6 text-gray-400 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {item.fileName}
                      </h4>
                      {renderStatus(item)}
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                      {item.fileType} • {item.ragId}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling
                      handleBuildChunks(item.ragId);
                    }}
                    disabled={isOperationInProgress}
                    className="text-blue-600 hover:text-blue-900 p-1"
                  >
                    <Play className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBuildEmbeddings(item.ragId);
                    }}
                    disabled={isOperationInProgress}
                    className="text-green-600 hover:text-green-900 p-1"
                  >
                    <Database className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteItem(item.ragId);
                    }}
                    disabled={isOperationInProgress}
                    className="text-red-600 hover:text-red-900 p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="border-t border-gray-100 pt-3">
                {renderLabels(item)}
                {getChunkError[item.ragId] && (
                  <div className="mt-2 text-sm text-red-600">
                    {getChunkError[item.ragId]}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ✅ Chunk Editor Modal retained */}
      {showChunkEditor && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Edit Chunks for {currentContext.ragItems?.find(item => item.ragId === showChunkEditor)?.fileName}
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Chunk Data (JSON)</label>
              <textarea
                value={editingChunks[showChunkEditor] || ''}
                onChange={(e) =>
                  setEditingChunks(prev => ({ ...prev, [showChunkEditor]: e.target.value }))
                }
                className="input-field h-64 resize-none font-mono text-sm"
                placeholder="Edit chunk data here..."
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => handleSaveChunks(showChunkEditor)}
                disabled={isOperationInProgress}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Edit3 className="h-4 w-4" />
                <span>Upload Chunks</span>
              </button>
              <button
                onClick={() => setShowChunkEditor(null)}
                disabled={isOperationInProgress}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileListTab;
